// Levels in Upside-down land. The id is fixed (progress is saved under it) – never change it once a level is published.
// Ball radius P = 15. ground/sky/furniture build statics with this world's skins.
import type { LevelData, ObjSpec, PartType, StaticData } from '../../core/types';
import { defineLevels } from '../../core/registry';
import { on } from '../../core/level-helpers';
import { under } from './shared';

const P = 15;
const ground = (x: number, y: number, w: number, h?: number, a?: number): StaticData => ({ x, y, w, h: h || 40, a: a || 0, skin: 'upsidedown_ground' });
const sky = (x: number, y: number, w: number, h?: number, a?: number): StaticData => ({ x, y, w, h: h || 26, a: a || 0, skin: 'upsidedown_cloud_shelf' });
const furniture = (x: number, y: number, w: number, h?: number, a?: number): StaticData => ({ x, y, w, h: h || 22, a: a || 0, skin: 'upsidedown_furniture' });
// A rope hangs from its anchor (ax, ay) instead of x/y; hang.t is typed as a part type here.
const rope = (o: { id: string; ax: number; ay: number; hang: { t: PartType; x: number; y: number } }): ObjSpec => ({ t: 'rope', ...o });
const L: LevelData[] = [];

// 1 – Up! – gravity is reversed here (gravity: -1), the ball falls up; an upside-down table bridges the gap under the ceiling
{
  const s1 = furniture(230, 500, 380, 22, -12);
  L.push({
    id: 'up', par: 1, tools: { plank: 2 }, learn: ['gravity', 'ramp'], gravity: -1,
    statics: [s1, ground(210, 100, 420, 200), ground(560, 20, 280, 40), ground(950, 100, 500, 200)],
    balls: [under(s1, -160, P)],
    goal: { type: 'bell', x: 780, y: 240, basket: true },
    objs: [{ t: 'duck', x: 640, y: 470 }],
    solution: [{ t: 'plank', x: 520, y: 400, a: -15 }],
  });
}

// 2 – The arrow – new tool: gravity arrow; the ball rolls over it and then falls up into the basket
{
  const s1 = ground(230, 520, 420, 30, 14);
  L.push({
    id: 'arrow', par: 1, tools: { upsidedown_arrow: 1 }, learn: ['gravity', 'direction'], intro: 'upsidedown_arrow',
    statics: [s1, ground(600, 760, 1200, 80), sky(880, 100, 640, 40), furniture(1185, 320, 30, 420)],
    balls: [on(s1, -180, P)],
    goal: { type: 'bell', x: 1100, y: 160, basket: true },
    objs: [{ t: 'duck', x: 300, y: 300 }],
    solution: [{ t: 'upsidedown_arrow', x: 700, y: 680 }],
  });
}
// 3 – The cloud – new tool: cloud; the umbrella throws the ball away, the cloud in front swallows the fall so the ball drops up into the basket
{
  const s1 = furniture(240, 640, 420, 22, -10);
  L.push({
    id: 'cloud', par: 1, tools: { upsidedown_cloud: 1 }, learn: ['materials', 'gravity'], intro: 'upsidedown_cloud', gravity: -1,
    statics: [s1, ground(600, 30, 1200, 60)],
    fixed: [{ t: 'pad', x: 790, y: 210, a: -90 }],
    balls: [under(s1, -180, P)],
    goal: { type: 'bell', x: 700, y: 100, basket: true },
    objs: [{ t: 'duck', x: 400, y: 300 }],
    solution: [{ t: 'upsidedown_cloud', x: 740, y: 230, a: 90 }],
  });
}
// 4 – The flip zone – new tool: flip zone; inside the purple field the ball falls the other way – up onto the shelf with the basket (plank is a decoy)
{
  const s1 = ground(200, 520, 360, 30, 12);
  L.push({
    id: 'flip-zone', par: 1, tools: { upsidedown_zone: 1, plank: 1 }, learn: ['gravity', 'direction'], intro: 'upsidedown_zone',
    statics: [s1, ground(600, 760, 1200, 80), ground(1030, 660, 340, 120)],
    balls: [on(s1, -150, P)],
    goal: { type: 'bowl', x: 1030, y: 600, w: 240 },
    objs: [{ t: 'duck', x: 500, y: 300 }],
    solution: [{ t: 'upsidedown_zone', x: 760, y: 660 }],
    fails: [[{ t: 'plank', x: 600, y: 600, a: -15 }], [{ t: 'upsidedown_zone', x: 300, y: 300 }]],
  });
}

// 5 – A short trip – zone vs. arrow: the zone lifts the ball onto the shelf for a moment, the arrow sends it away for good (arrow is the decoy)
{
  const s1 = ground(1000, 520, 360, 30, -12);
  L.push({
    id: 'short-trip', par: 1, tools: { upsidedown_arrow: 1, upsidedown_zone: 1 }, learn: ['gravity', 'direction'],
    statics: [s1, ground(600, 760, 1200, 80), ground(170, 660, 340, 120)],
    balls: [on(s1, 150, P)],
    goal: { type: 'bowl', x: 170, y: 600, w: 240 },
    objs: [{ t: 'duck', x: 700, y: 300 }],
    solution: [{ t: 'upsidedown_zone', x: 440, y: 660 }],
    fails: [[{ t: 'upsidedown_arrow', x: 440, y: 680 }], [{ t: 'upsidedown_arrow', x: 600, y: 680 }]],
  });
}
// 6 – The pot – the pot (stone) falls up onto the seesaw and flings the ball down into the basket (plank is a decoy)
{
  L.push({
    id: 'pot', par: 1, tools: { stone: 1, plank: 1 }, learn: ['lever', 'gravity'], intro: 'seesaw', gravity: -1,
    statics: [furniture(522, 215, 10, 40), ground(400, 100, 60, 200), ground(600, 20, 1200, 40)],
    fixed: [{ t: 'seesaw', x: 400, y: 240, a: -20 }],
    balls: [{ x: 495, y: 230, r: P }],
    goal: { type: 'bell', x: 510, y: 430 },
    objs: [{ t: 'duck', x: 515, y: 500 }],
    solution: [{ t: 'stone', x: 310, y: 550 }],
    fails: [[{ t: 'stone', x: 480, y: 550 }], [{ t: 'plank', x: 330, y: 400, a: 15 }]],
  });
}
// 7 – Down again – the ball slides along the ceiling; the arrow flips gravity so it falls down into the basket (zone is a decoy)
{
  const s1 = furniture(230, 260, 420, 22, -12);
  L.push({
    id: 'down-again', par: 1, tools: { upsidedown_zone: 1, upsidedown_arrow: 1 }, learn: ['gravity', 'direction'], gravity: -1,
    statics: [s1, ground(600, 30, 1200, 60), ground(600, 780, 1200, 40), furniture(1185, 410, 30, 700)],
    balls: [under(s1, -180, P)],
    goal: { type: 'bowl', x: 1060, y: 760, w: 220 },
    objs: [{ t: 'duck', x: 600, y: 400 }],
    solution: [{ t: 'upsidedown_arrow', x: 900, y: 110 }],
    fails: [[{ t: 'upsidedown_zone', x: 900, y: 140 }]],
  });
}

// 8 – Hop and flip – combo: the umbrella (pad) bounces the ball over the wall, the arrow flips gravity, it falls up into the basket (plank is a decoy)
{
  const s1 = furniture(140, 260, 220, 20, 10);
  L.push({
    id: 'hop-and-flip', par: 2, tools: { pad: 1, upsidedown_arrow: 1, plank: 1 }, learn: ['spring', 'gravity', 'direction'],
    statics: [s1, ground(600, 780, 1200, 40), furniture(640, 640, 26, 280), ground(820, 30, 760, 60), furniture(1185, 300, 30, 480)],
    balls: [on(s1, -80, P)],
    goal: { type: 'bell', x: 1100, y: 100, basket: true },
    objs: [{ t: 'duck', x: 900, y: 600 }],
    solution: [{ t: 'pad', x: 400, y: 600, a: 15 }, { t: 'upsidedown_arrow', x: 720, y: 460 }],
    fails: [[{ t: 'pad', x: 400, y: 600, a: 15 }], [{ t: 'upsidedown_arrow', x: 300, y: 500 }], [{ t: 'plank', x: 400, y: 600, a: 15 }, { t: 'upsidedown_arrow', x: 720, y: 460 }]],
  });
}

// 9 – Tip and zone – combo: the pot tips the ball up, the flip zone carries it on up to the basket under the ceiling (arrow is a decoy)
{
  L.push({
    id: 'tip-and-zone', par: 2, tools: { stone: 1, upsidedown_zone: 1, upsidedown_arrow: 1 }, learn: ['lever', 'gravity', 'direction'],
    statics: [furniture(522, 585, 10, 40), ground(400, 700, 60, 200), ground(600, 790, 1200, 20), ground(520, 30, 400, 60)],
    fixed: [{ t: 'seesaw', x: 400, y: 560, a: 20 }],
    balls: [{ x: 495, y: 570, r: P }],
    goal: { type: 'bell', x: 520, y: 100, basket: true },
    objs: [{ t: 'duck', x: 800, y: 400 }],
    solution: [{ t: 'stone', x: 310, y: 250 }, { t: 'upsidedown_zone', x: 510, y: 330 }],
    fails: [[{ t: 'stone', x: 310, y: 250 }], [{ t: 'upsidedown_zone', x: 510, y: 330 }], [{ t: 'stone', x: 470, y: 250 }, { t: 'upsidedown_zone', x: 510, y: 330 }]],
  });
}

// 10 – Snip and flip – chain reaction: helper ball presses the button, the rope is cut, the pot tips the ball up – the arrow sends it into the basket
{
  const sb = furniture(150, 150, 180, 20, 6);
  L.push({
    id: 'snip-and-flip', par: 2, tools: { plank: 2, upsidedown_arrow: 1 }, learn: ['chain_reaction', 'lever', 'gravity'],
    statics: [sb, furniture(520, 430, 170, 20), furniture(598, 400, 14, 60), furniture(636, 668, 10, 40), ground(760, 758, 50, 180), ground(600, 790, 1200, 20), ground(660, 30, 300, 60)],
    fixed: [{ t: 'seesaw', x: 760, y: 640, a: -20 }],
    balls: [{ x: 665, y: 650, r: P }, on(sb, -70, 13, { helper: true })],
    goal: { type: 'bell', x: 660, y: 100, basket: true },
    objs: [{ t: 'button', x: 555, y: 405, act: { cut: 'r1' } }, rope({ id: 'r1', ax: 870, ay: 120, hang: { t: 'stone', x: 870, y: 300 } }), { t: 'duck', x: 330, y: 195 }],
    solution: [{ t: 'plank', x: 340, y: 260, a: 15 }, { t: 'upsidedown_arrow', x: 680, y: 580 }],
    fails: [[{ t: 'plank', x: 340, y: 260, a: 15 }], [{ t: 'upsidedown_arrow', x: 680, y: 580 }]],
  });
}

// 11 – Honey – the ball zooms up the slope past the basket; honey (paint) slows it so it falls up into the basket (cloud is a decoy)
{
  const s1 = furniture(300, 550, 520, 22, -25);
  L.push({
    id: 'honey', par: 1, tools: { paint: 1, upsidedown_cloud: 1 }, learn: ['friction', 'ramp', 'gravity'], intro: 'paint', gravity: -1,
    statics: [s1, ground(820, 140, 640, 30)],
    balls: [under(s1, -230, P)],
    goal: { type: 'bell', x: 610, y: 195, basket: true },
    objs: [{ t: 'duck', x: 578, y: 438 }],
    solution: [{ t: 'paint', x: 420, y: 510 }],
    fails: [[{ t: 'upsidedown_cloud', x: 800, y: 175, a: 0 }]],
  });
}

// 12 – The pulley – the pot in the bucket lifts the gate, the ball rolls down – the arrow flips gravity so it falls up into the basket (plank is a decoy)
{
  const s1 = furniture(360, 400, 640, 22, 10);
  L.push({
    id: 'pulley', par: 2, tools: { stone: 1, upsidedown_arrow: 1, plank: 1 }, learn: ['pulley', 'gravity', 'ramp'],
    statics: [s1, ground(600, 790, 1200, 20), ground(890, 30, 600, 60), furniture(1185, 360, 30, 600)],
    balls: [on(s1, -250, P)],
    goal: { type: 'bell', x: 1105, y: 100, basket: true },
    objs: [{ t: 'pulley', x: 725, y: 110, A: { x: 470, y: 330, type: 'gate', h: 150 }, B: { x: 1000, y: 280, type: 'bucket' }, min: 0, range: 170 }, { t: 'duck', x: 633, y: 398 }],
    solution: [{ t: 'stone', x: 1000, y: 120 }, { t: 'upsidedown_arrow', x: 830, y: 560 }],
    fails: [[{ t: 'stone', x: 1000, y: 120 }], [{ t: 'upsidedown_arrow', x: 830, y: 560 }], [{ t: 'plank', x: 1000, y: 160, a: 0 }, { t: 'upsidedown_arrow', x: 830, y: 560 }]],
  });
}
// 13 – Into the basket – the pot falls up onto the seesaw and throws the ball down; the arrow flips gravity so it keeps falling into the basket (cloud is a decoy)
{
  L.push({
    id: 'into-the-basket', par: 2, tools: { stone: 1, upsidedown_arrow: 1, upsidedown_cloud: 1 }, learn: ['lever', 'gravity', 'direction'], gravity: -1,
    statics: [furniture(522, 215, 10, 40), ground(400, 100, 60, 200), ground(600, 20, 1200, 40), ground(600, 790, 1200, 20)],
    fixed: [{ t: 'seesaw', x: 400, y: 240, a: -20 }],
    balls: [{ x: 495, y: 230, r: P }],
    goal: { type: 'bowl', x: 560, y: 780, w: 200 },
    objs: [{ t: 'duck', x: 800, y: 500 }],
    solution: [{ t: 'stone', x: 310, y: 550 }, { t: 'upsidedown_arrow', x: 530, y: 460 }],
    fails: [[{ t: 'stone', x: 310, y: 550 }], [{ t: 'upsidedown_arrow', x: 530, y: 460 }], [{ t: 'stone', x: 310, y: 550 }, { t: 'upsidedown_cloud', x: 540, y: 520, a: 0 }]],
  });
}

// 14 – The toaster – combine: the table guides the ball into the toaster, it pops up, the arrow flips gravity and the cloud stops it under the basket (zone is a decoy)
{
  const s1 = furniture(150, 300, 220, 20, 10);
  L.push({
    id: 'toaster', par: 3, tools: { plank: 1, upsidedown_arrow: 1, upsidedown_cloud: 1, upsidedown_zone: 1 }, learn: ['mechanism', 'gravity', 'materials'],
    statics: [s1, ground(570, 700, 220, 40), ground(600, 790, 1200, 20), ground(900, 30, 600, 60)],
    fixed: [{ t: 'pad', x: 800, y: 76, a: 180 }],
    balls: [on(s1, -80, P)],
    goal: { type: 'bell', x: 960, y: 100, basket: true },
    objs: [{ t: 'toaster', x: 570, y: 640, pop: [4, -15] }, { t: 'duck', x: 740, y: 230 }],
    solution: [{ t: 'plank', x: 320, y: 450, a: 15 }, { t: 'upsidedown_arrow', x: 720, y: 250 }, { t: 'upsidedown_cloud', x: 730, y: 130, a: 0 }],
    fails: [[{ t: 'plank', x: 320, y: 450, a: 15 }, { t: 'upsidedown_arrow', x: 720, y: 250 }], [{ t: 'plank', x: 320, y: 450, a: 15 }, { t: 'upsidedown_cloud', x: 730, y: 130, a: 0 }]],
  });
}

// 15 – The big machine – master level: toaster, table, umbrella, button, rope, pot, seesaw and finally the gravity arrow (zone and cloud are decoys)
{
  const s1 = furniture(150, 250, 260, 20, 12);
  L.push({
    id: 'big-machine', par: 3, tools: { plank: 1, pad: 1, upsidedown_arrow: 1, upsidedown_zone: 1, upsidedown_cloud: 1 }, learn: ['mechanism', 'chain_reaction', 'lever', 'gravity'], time: 26,
    statics: [s1, ground(300, 750, 220, 40), furniture(830, 440, 140, 20, -6), furniture(757, 415, 14, 70), furniture(1153, 668, 10, 40), ground(1030, 758, 50, 180), ground(1000, 30, 400, 60), ground(600, 790, 1200, 20)],
    fixed: [{ t: 'seesaw', x: 1030, y: 640, a: 20 }],
    balls: [on(s1, -100, P), { x: 1124, y: 652, r: 13, helper: true }],
    goal: { type: 'bell', x: 1110, y: 100, basket: true, any: true },
    objs: [
      { t: 'toaster', x: 300, y: 690, pop: [3, -17], funnel: true },
      { t: 'button', x: 796, y: 418, a: -6, act: { cut: 'r1' } },
      rope({ id: 'r1', ax: 925, ay: 200, hang: { t: 'stone', x: 925, y: 360 } }),
      { t: 'duck', x: 367, y: 300 },
    ],
    solution: [{ t: 'plank', x: 460, y: 440, a: -30 }, { t: 'pad', x: 620, y: 610, a: 0 }, { t: 'upsidedown_arrow', x: 1150, y: 520 }],
    fails: [[{ t: 'plank', x: 460, y: 440, a: -30 }, { t: 'pad', x: 620, y: 610, a: 0 }], [{ t: 'pad', x: 620, y: 610, a: 0 }, { t: 'upsidedown_arrow', x: 1150, y: 520 }],
      [{ t: 'plank', x: 460, y: 440, a: -30 }, { t: 'upsidedown_arrow', x: 1150, y: 520 }]],
  });
}

export const levels = defineLevels(L);
